import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Bookmark, ExternalLink, Sparkles, Zap, Download, Copy, Trash2 } from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HowItWorks from "../components/HowItWorks";

const CATS = ["all", "curiosity", "history", "biology", "physics", "culture", "space", "food", "language", "geography", "animals", "art"];

function host(u) {
  try { return new URL(u).host.replace(/^www\./, ""); } catch { return u; }
}

function downloadCSV(rows, filename = "fun-facts.csv") {
  const header = ["title", "fact", "category", "surprise", "obscurity", "fun_rank", "source_1", "source_2", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [header.join(",")]
    .concat(rows.map((r) => header.map((k) => esc(r[k])).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

export default function FunFactsPage() {
  const qc = useQueryClient();
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("fun_rank");
  const { data: stats } = useQuery({
    queryKey: ["facts-stats"],
    queryFn: () => api.get("/fun-facts/stats").then((r) => r.data),
    refetchInterval: 15000,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["facts", cat, sort],
    queryFn: () =>
      api
        .get(`/fun-facts/items?limit=120&sort=${sort}${cat !== "all" ? `&category=${cat}` : ""}`)
        .then((r) => r.data),
    refetchInterval: 15000,
  });

  const trigger = useMutation({
    mutationFn: () => api.post("/fun-facts/trigger").then((r) => r.data),
    onSuccess: () => toast.success("FUN_FACTS_QUEUED · check back in ~30s"),
  });

  const [saved, setSaved] = useState({});  // { factId: saveId }

  const saveOne = async (it) => {
    try {
      const { data } = await api.post("/saves", {
        layer: "custom", title: it.title, text: it.fact,
        payload: { surprise: it.surprise, source_1: it.source_1, source_2: it.source_2 },
        note: "fun-fact",
      });
      setSaved((s) => ({ ...s, [it.id]: data?.id || true }));
      toast.success("SAVED", { description: "Find it in My Library." });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "unknown error";
      toast.error("SAVE_FAILED", { description: msg });
    }
  };

  const unsaveOne = async (it) => {
    const sid = saved[it.id];
    if (!sid || sid === true) return;
    try {
      await api.delete(`/saves/${sid}`);
      setSaved((s) => { const c = { ...s }; delete c[it.id]; return c; });
      toast.success("REMOVED_FROM_LIBRARY");
    } catch (e) {
      toast.error("DELETE_FAILED", { description: e?.response?.data?.detail || e?.message });
    }
  };

  const livePct = useMemo(
    () => Math.min(100, ((stats?.today_count || 0) / (stats?.today_target || 10)) * 100),
    [stats],
  );

  return (
    <div data-testid="fun-facts-page" className="p-4 space-y-4">
      <header className="flex flex-wrap items-end gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-mono inline-flex items-center gap-2">
            <Sparkles size={12} /> FUN_FACTS · DAILY_LAYER
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">Oddly-true daily facts</h1>
          <p className="text-[12px] text-zinc-500 max-w-2xl mt-1">
            10 surprising real-world facts every day — outside the 10 main libraries.
            Out-of-the-ordinary by design.
          </p>
        </div>
        <div className="ml-auto flex items-end gap-4">
          <Stat label="TODAY" value={`${stats?.today_count ?? 0} / ${stats?.today_target ?? 10}`}
            sub="auto top-up" color="text-amber-300" />
          <div className="h-[3px] w-40 bg-[#1a1a1a] self-end mb-1">
            <div className="bg-amber-400" style={{ width: `${livePct}%`, height: "100%" }} />
          </div>
          <Stat label="LIVE" value={stats?.active ?? 0} sub="30-day window" color="text-emerald-300" />
          <button
            data-testid="trigger-facts"
            onClick={() => trigger.mutate()}
            disabled={trigger.isPending}
            className="h-9 px-3 text-[11px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Zap size={11} /> {trigger.isPending ? "QUEUEING…" : "DRAW_MORE"}
          </button>
        </div>
      </header>

      <HowItWorks
        id="facts"
        tag="ODDLY-TRUE · 10/DAY · 30-DAY ROLLING"
        headline="A daily curiosity layer outside the 10 main libraries. Ranked by surprise + obscurity — perfect for dinner-party ammo."
        steps={[
          { accent: "amber", label: "Skim today's draw", body: "10 fresh facts added daily — each card shows OBSC (obscurity) and RANK (combined surprise score)." },
          { accent: "purple", label: "Pick a category", body: "Filter by HISTORY · BIOLOGY · SPACE · FOOD · LANGUAGE · ART or sort by FUN vs NEWEST." },
          { accent: "emerald", label: "Save the best ones", body: "Bookmark to MY_LIB so your favorites stay even after the 30-day window expires." },
          { accent: "sky", label: "DRAW_MORE", body: "Out of facts? Click DRAW_MORE to top-up immediately. Otherwise the daily scheduler refills automatically." },
          { accent: "rose", label: "Export CSV", body: "Hit the CSV button to download the active fact list — great for newsletters or quiz nights." },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2 border-y border-[#27272A] px-3 py-2 bg-[#0E0E0E]">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">CAT</span>
        {CATS.map((c) => (
          <button
            key={c}
            data-testid={`facts-cat-${c}`}
            onClick={() => setCat(c)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (cat === c ? "border-amber-400 bg-amber-400 text-black" : "border-[#27272A] text-zinc-400 hover:text-white")
            }
          >
            {c}
          </button>
        ))}
        <span className="ml-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">SORT</span>
        {[
          { v: "fun_rank", l: "FUN" },
          { v: "surprise", l: "SURPRISE" },
          { v: "obscurity", l: "OBSCURITY" },
          { v: "created_at", l: "NEWEST" },
        ].map((s) => (
          <button
            key={s.v}
            onClick={() => setSort(s.v)}
            className={
              "h-7 px-2 text-[10px] font-mono uppercase tracking-wider border " +
              (sort === s.v ? "border-white bg-white text-black" : "border-[#27272A] text-zinc-400 hover:text-white")
            }
          >
            {s.l}
          </button>
        ))}
        <button
          data-testid="facts-export-csv"
          onClick={() => downloadCSV(items)}
          className="ml-auto h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] inline-flex items-center gap-1"
        >
          <Download size={11} /> CSV
        </button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 && (
          <div className="md:col-span-2 grid-bg p-10 text-center text-[12px] font-mono text-zinc-500">
            NO_FACTS_YET · click DRAW_MORE or wait for the scheduler
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`fact-card-${it.id}`}
            className="border border-[#27272A] bg-[#0E0E0E] p-3 hover:bg-[#141414]"
          >
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
              <span className="text-amber-300 border border-amber-300/40 px-1">{it.category}</span>
              <span className="text-zinc-500">SURPRISE {it.surprise}</span>
              <span className="text-zinc-500">· OBSC {it.obscurity}</span>
              <span className="ml-auto text-zinc-600 tabnum">RANK {it.fun_rank}</span>
            </div>
            <div className="mt-2 font-mono text-[15px] text-white leading-snug">{it.title}</div>
            <div className="mt-1 text-zinc-300 text-[12px] leading-relaxed">{it.fact}</div>
            <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-zinc-400">
              <a href={it.source_1} target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1">
                S1 · {host(it.source_1)} <ExternalLink size={10} />
              </a>
              <a href={it.source_2} target="_blank" rel="noopener noreferrer" className="hover:text-white inline-flex items-center gap-1">
                S2 · {host(it.source_2)} <ExternalLink size={10} />
              </a>
              {saved[it.id] ? (
                <button
                  data-testid={`unsave-fact-${it.id}`}
                  onClick={() => unsaveOne(it)}
                  className="ml-auto inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-rose-400/60 text-rose-300 hover:bg-rose-500/10"
                  title="Remove from My Library"
                >
                  <Trash2 size={10} /> REMOVE
                </button>
              ) : (
                <button
                  data-testid={`save-fact-${it.id}`}
                  onClick={() => saveOne(it)}
                  className="ml-auto inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-wider border border-purple-400/60 text-purple-300 hover:bg-purple-500/10"
                  title="Save to My Library"
                >
                  <Bookmark size={10} /> SAVE
                </button>
              )}
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`${it.title}\n${it.fact}\n${it.source_1}\n${it.source_2}`);
                  toast.success("COPIED");
                }}
                className="text-zinc-500 hover:text-white"
                title="Copy"
              >
                <Copy size={12} />
              </button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-2 min-w-[120px]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{label}</div>
      <div className={"text-2xl font-mono tabnum mt-1 " + (color || "text-white")}>{value}</div>
      <div className="text-[10px] text-zinc-500 font-mono mt-1">{sub}</div>
    </div>
  );
}
